import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
} from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DropdownOptions,
  Icon,
  ModalProvider,
  toast,
  TooltipPopup,
  ViewerContext,
} from "dash-ui";
import {
  useGetIdxFeedDetailsQuery,
  useGetPropertiesForInputQueryQuery,
  useGetUserPublicPortfolioFoldersQuery,
  useUpdateIdxFeedMutation,
} from "gql/generated.graphql";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FiAlertTriangle,
  FiChevronDown,
  FiCopy,
  FiLoader,
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  IdxFeedSearchBarContentEnum,
  IdxFeedTypeEnum,
  IdxFeedPortfolioContentEnum,
} from "dash-common";

dayjs.extend(utc);
dayjs.utc();

// IDX Form Schema
const idxFeedFormSchema = z
  .object({
    name: z.string().min(1, "Name is required."),
    type: z.nativeEnum(IdxFeedTypeEnum),
    propertyId: z.string().optional().nullable(),
    portfolioFolderId: z.string().optional().nullable(),
    propertiesPerPage: z.coerce.number().optional(),
    searchBarContent: z.nativeEnum(IdxFeedSearchBarContentEnum),
    portfolioContent: z
      .nativeEnum(IdxFeedPortfolioContentEnum)
      .optional()
      .nullable(),
  })
  .refine(
    (data) => {
      if (data.type === "SEARCH_BAR") return !!data.searchBarContent;
      return true;
    },
    {
      message: "Search bar content is required.",
      path: ["searchBarContent"],
    }
  )
  .refine(
    (data) => {
      if (data.type === "SINGLE_PROPERTY") return !!data.propertyId;
      return true;
    },
    {
      message: "Property is required.",
      path: ["propertyId"],
    }
  )
  .refine(
    (data) => {
      if (data.type === "PORTFOLIO") return !!data.portfolioFolderId;
      return true;
    },
    {
      message: "Portfolio is required.",
      path: ["portfolioFolderId"],
    }
  )
  .refine(
    (data) => {
      if (data.type === "PORTFOLIO") return !!data.propertiesPerPage;
      return true;
    },
    {
      message: "Page length is required.",
      path: ["propertiesPerPage"],
    }
  )
  .refine(
    (data) => {
      if (data.type === "PORTFOLIO") return !!data.portfolioContent;
      return true;
    },
    {
      message: "Portfolio display content is required.",
      path: ["portfolioContent"],
    }
  );

// Types
type IdxFeedFormData = z.infer<typeof idxFeedFormSchema>;

// Chart component
function IdxActivityLineChart({ chartData, dateRange, onChartClick }) {
  return (
    <div className="chart-container flex flex-col gap-1 items-center">
      <Line
        height={300}
        width={400}
        data={chartData}
        options={{
          onClick: (event, element, chart) => {
            // get chart's clicked point
            const activePoints = chart.getElementsAtEventForMode(
              event.native!,
              "nearest",
              { intersect: true },
              false
            );
            if (activePoints.length > 0) {
              const index = activePoints[0].index;
              const label = chartData.labels[index];
              onChartClick(label);
            }
          },
          plugins: {
            title: {
              display: true,
              text: `Properties clicked between ${dateRange.startDate.toLocaleDateString()} and ${dateRange.endDate.toLocaleDateString()}`,
            },
          },
        }}
      />
    </div>
  );
}

export function IdxDetailsPage() {
  // ==> Commented code related to IDX Analytics <==
  //
  // const [dateRange, setDateRange] = useState<{
  //   startDate: Date;
  //   endDate: Date;
  // }>({
  //   startDate: dayjs().subtract(30, "day").toDate(),
  //   endDate: dayjs().startOf("day").toDate(),
  // });
  // const [targetDate, setTargetDate] = useState("");
  //
  // const getIdxActivity = useGetIdxActivityQuery({
  //   variables: {
  //     idxId: getIdxQuery.data?.getIdxSettings._id,
  //     dateRange: {
  //       startDate: dayjs.utc(dateRange.startDate).startOf("d").toDate(),
  //       endDate: dayjs
  //         .utc(dateRange.endDate)
  //         .startOf("d")
  //         .add(1, "day")
  //         .toDate(),
  //     },
  //   },
  //   skip: !getIdxQuery.data,
  // });
  //
  // const chartData = {
  //   labels: idxActivityData?.map((activity) =>
  //     dayjs.utc(activity.date).format("MM/DD/YYYY")
  //   ),
  //   datasets: [
  //     {
  //       label: "Property Clicks",
  //       data: idxActivityData?.map((date) => date.totalClicks),
  //       borderColor: "rgb(2 132 199)",
  //       pointBackgroundColor: "rgb(2 132 199)",
  //       borderWidth: 2,
  //     },
  //   ],
  // };
  // const tableData = idxActivityData?.find((e) =>
  //   dayjs(targetDate).isSame(dayjs(e.date), "day")
  // );
  //
  // function handleDateRangeChange({ startDate, endDate }: DateRangeInput) {
  //   setDateRange({
  //     startDate: startDate,
  //     endDate: endDate,
  //   });
  // }
  // function handleResetDateRange() {
  //   setDateRange({
  //     startDate: dayjs().subtract(30, "day").toDate(),
  //     endDate: dayjs().startOf("day").toDate(),
  //   });
  //   setTargetDate("");
  // }
  // function handleChartClick(date: string) {
  //   setTargetDate((cur) => (date === cur ? "" : date));
  // }
  //
  //==> Comented code related to IDX Analytics <==

  // Filtering Properties and Portfolios
  const [propertiesSearch, setPropertiesSearch] = useState<string>("");
  const [portfolioSearch, setPortfolioSearch] = useState<string>("");

  // Iframe code & source states
  const [iframeSource, setIframeSource] = useState<string>("");
  const iframeCode = `<iframe src="${iframeSource}" frameborder="0" allowfullscreen height="100%" width="100%"></iframe>`;

  // Hooks
  const loggedUser = useContext(ViewerContext);
  const navigate = useNavigate();
  const idxFeedForm = useForm<IdxFeedFormData>({
    resolver: zodResolver(idxFeedFormSchema),
  });
  const params = useParams();
  const idxFeedId = params.idxFeedId;

  // IDX feed data
  const getIdxFeedQuery = useGetIdxFeedDetailsQuery({
    variables: { idxFeedId },
  });
  const idxFeedData = getIdxFeedQuery.data?.getIdxFeedDetails;
  const [updateIdxFeedMutation, updateIdxFeedMutationStates] =
    useUpdateIdxFeedMutation();

  // Portfolio listing data
  const getPortfolioFoldersQuery = useGetUserPublicPortfolioFoldersQuery({
    fetchPolicy: "network-only",
  });
  const portfolios =
    getPortfolioFoldersQuery.data?.getUserPublicPortfolioFolders || [];
  const filteredPortfolios = portfolioSearch
    ? portfolios.filter((portfolio) =>
        portfolio.name
          ?.toLowerCase()
          .includes(portfolioSearch.toLocaleLowerCase())
      )
    : portfolios;

  // Properties listing data
  const getPropertiesQuery = useGetPropertiesForInputQueryQuery({
    variables: {
      pagination: {
        skip: 0,
        limit: 30,
      },
      sort: {
        fieldName: "name",
        sortOrder: 1,
      },
      filter: {
        unitNumberOfBedrooms: {},
        unitNumberOfBathrooms: {},
        searchText: propertiesSearch.length > 3 ? propertiesSearch : undefined,
      },
    },
  });
  const properties =
    getPropertiesQuery.data?.getBrokerProperties.collection || [];

  // Handle Sumbmit Form
  async function saveIdxFeed(data: IdxFeedFormData) {
    await updateIdxFeedMutation({
      variables: {
        input: {
          idxFeedId,
          name: data.name,
          portfolioFolderId: data.portfolioFolderId,
          propertiesPerPage: data.propertiesPerPage,
          propertyId: data.propertyId,
          type: data.type,
          searchBarContent: data.searchBarContent,
          portfolioContent: data.portfolioContent,
        },
      },
      onCompleted: () => {
        toast.success("Exchange Feed updates successfully!");
      },
      onError: () => {
        toast.error("Error on update Exchange Feed.");
      },
    });
  }

  // Set initial states for react hook form
  useEffect(() => {
    if (idxFeedData) {
      idxFeedForm.reset({
        name: idxFeedData.name,
        portfolioFolderId: idxFeedData.portfolioFolderId,
        propertiesPerPage: idxFeedData.propertiesPerPage || 3,
        searchBarContent:
          idxFeedData.searchBarContent as IdxFeedSearchBarContentEnum,
        portfolioContent:
          idxFeedData.portfolioContent as IdxFeedPortfolioContentEnum,
        propertyId: idxFeedData.propertyId,
        type: idxFeedData.type as IdxFeedFormData["type"],
      });

      if (
        idxFeedData.type !== IdxFeedTypeEnum.SEARCH_BAR &&
        !idxFeedData.portfolioFolderId &&
        !idxFeedData.propertyId
      )
        return;

      setIframeSource(
        `${process.env["REACT_APP_REST"]}/idx/embedded/${
          idxFeedData._id
        }?timestamp=${Date.now()}`
      );
    }
  }, [idxFeedData]);

  // Redirect user if has no active subscription
  if (loggedUser?.subscription?.subscriptionProduct?.name !== "DASH Exchange") {
    return (
      <>
        <ModalProvider>
          <>
            <div className="flex flex-col h-full w-full text-slate-800">
              <header className="flex">
                <h1 className="font-bold text-xl">Exchange</h1>
              </header>

              <main className="flex flex-col items-center4">
                <p className="text-xl">
                  Get access to Exchange Feeds with the DASH Exchange plan!
                </p>
                <button
                  onClick={() => {
                    navigate("/profile?tab=BILLING");
                  }}
                  className="bg-sky-600 text-white rounded px-4 h-10 max-w-max"
                >
                  Manage your subscription
                </button>
              </main>
            </div>
            <DropdownOptions />
            <TooltipPopup />
          </>
        </ModalProvider>
      </>
    );
  }

  // HTML Component
  return (
    <ModalProvider>
      <>
        <DropdownOptions />
        <TooltipPopup />
        <form
          onSubmit={idxFeedForm.handleSubmit(saveIdxFeed)}
          className="flex flex-col text-slate-800 pb-10 w-full max-w-screen-xl gap-4"
        >
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2">
            <Link
              to="/exchange"
              className="flex items-center gap-2 hover:underline text-sky-600 h-8 rounded"
            >
              <Icon name="dashExchange" />
              <span>Exchange</span>
            </Link>
            <span className="text-slate-600">/</span>
            <span className="text-slate-600">Feed Details</span>
          </div>

          {/* Header */}
          <header className="flex items-center justify-between">
            <h1 className="text-lg font-bold">Feed Details</h1>
            <button
              className="h-10 px-4 rounded text-white bg-sky-600 disabled:bg-slate-300 hover:bg-sky-500"
              disabled={
                !idxFeedForm.formState.isDirty ||
                updateIdxFeedMutationStates.loading
              }
            >
              Save
            </button>
          </header>

          {/* Render loading state */}
          {getIdxFeedQuery.loading && (
            <div className="flex items-center justify-center h-96 w-full animate-pulse gap-4">
              <FiLoader className="animate-spin" />
              <span>Loading...</span>
            </div>
          )}

          {/* Render form if everything is ok */}
          {!getIdxFeedQuery.loading && !getIdxFeedQuery.error && (
            <>
              {/* Form */}
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-slate-500 text-sm">Feed Name*</label>
                    <input
                      type="text"
                      placeholder="Feed Name"
                      className="h-10 border border-slate-300 rounded px-4"
                      {...idxFeedForm.register("name")}
                    />
                    <span className="text-red-600">
                      {idxFeedForm.formState.errors.name?.message}
                    </span>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-slate-500 text-sm">Feed Type*</label>
                    <select
                      placeholder="Feed Type"
                      className="h-10 border border-slate-300 rounded px-4"
                      {...idxFeedForm.register("type")}
                    >
                      <option value={IdxFeedTypeEnum.PORTFOLIO}>
                        Portfolio
                      </option>
                      <option value={IdxFeedTypeEnum.SINGLE_PROPERTY}>
                        Single Property
                      </option>
                      <option value={IdxFeedTypeEnum.SEARCH_BAR}>
                        Search Bar
                      </option>
                    </select>
                    <span className="text-red-600">
                      {idxFeedForm.formState.errors.type?.message}
                    </span>
                  </div>
                </div>

                {/* Render in case property */}
                {idxFeedForm.watch("type") ===
                  IdxFeedTypeEnum.SINGLE_PROPERTY && (
                  <div className="grid grid-cols-1">
                    <div className="flex flex-col">
                      <label className="text-sm text-slate-500">Property</label>
                      <Combobox
                        value={idxFeedForm.watch("propertyId")}
                        onChange={(value) => {
                          idxFeedForm.setValue("propertyId", value || "", {
                            shouldDirty: true,
                          });
                        }}
                      >
                        <div
                          className={`relative flex items-center h-10 rounded border border-slate-300 bg-white`}
                        >
                          <ComboboxInput
                            onChange={(event) =>
                              setPropertiesSearch(event.target.value)
                            }
                            displayValue={(value) => {
                              const selectedProperty = properties.find(
                                (property) => property._id === value
                              );
                              return selectedProperty?.name || "";
                            }}
                            className="bg-transparent h-10 w-full outline-none px-4"
                            placeholder="Search and select a property"
                          />
                          <ComboboxButton className="absolute right-0 w-10 h-10 flex items-center justify-center">
                            <FiChevronDown />
                          </ComboboxButton>
                        </div>

                        <ComboboxOptions
                          anchor="bottom start"
                          className="bg-white border shadow rounded cursor-pointer w-[var(--input-width)]"
                        >
                          {properties.map((property, index) => {
                            return (
                              <ComboboxOption
                                key={index}
                                value={property._id}
                                className="h-8 data-[focus]:bg-sky-600 data-[focus]:text-white flex items-center px-4"
                              >
                                {property.name}
                              </ComboboxOption>
                            );
                          })}
                        </ComboboxOptions>
                      </Combobox>
                      <span className="text-red-600">
                        {idxFeedForm.formState.errors.propertyId?.message}
                      </span>
                      <span className="text-sky-600 text-sm">
                        Please write at least 3 characters to see the results
                      </span>
                    </div>
                  </div>
                )}

                {/* Render in case portfolio */}
                {idxFeedForm.watch("type") === IdxFeedTypeEnum.PORTFOLIO && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-slate-500 text-sm">Layout*</label>
                      <select
                        className={`h-10 px-4 rounded border border-slate-300`}
                        {...idxFeedForm.register("portfolioContent")}
                      >
                        <option value={"LIST_IN_GRID"}>Grid Default</option>
                        <option value={"LIST_IN_MAP"}>Map Default</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-slate-500">
                        Properties per page
                      </label>
                      <select
                        className="h-10 px-4 rounded border border-slate-300"
                        {...idxFeedForm.register("propertiesPerPage", {
                          valueAsNumber: true,
                        })}
                      >
                        <option value={3}>3</option>
                        <option value={6}>6</option>
                        <option value={9}>9</option>
                        <option value={12}>12</option>
                      </select>
                      <span className="text-red-600">
                        {
                          idxFeedForm.formState.errors.propertiesPerPage
                            ?.message
                        }
                      </span>
                    </div>

                    <div className="flex flex-col col-span-2 gap-1">
                      <label className="text-sm text-slate-500">
                        Portfolio
                      </label>
                      <Combobox
                        value={idxFeedForm.watch("portfolioFolderId")}
                        onChange={(value) =>
                          idxFeedForm.setValue("portfolioFolderId", value!, {
                            shouldDirty: true,
                          })
                        }
                      >
                        <div
                          className={`relative flex items-center h-10 rounded border border-slate-300 bg-white`}
                        >
                          <ComboboxInput
                            onChange={(event) =>
                              setPortfolioSearch(event.target.value)
                            }
                            displayValue={(value) => {
                              const selectedPortfolio = portfolios.find(
                                (portfolio) => portfolio._id === value
                              );
                              return selectedPortfolio?.name || "";
                            }}
                            className="bg-transparent h-10 w-full outline-none px-4"
                            placeholder="Search and select one of your portfolios"
                          />
                          <ComboboxButton className="absolute right-0 w-10 h-10 flex items-center justify-center">
                            <FiChevronDown />
                          </ComboboxButton>
                        </div>
                        <ComboboxOptions
                          anchor="bottom start"
                          className="bg-white border shadow rounded cursor-pointer w-[var(--input-width)]"
                        >
                          {filteredPortfolios.map((portfolio, index) => (
                            <ComboboxOption
                              key={index}
                              value={portfolio._id}
                              className="h-8 data-[focus]:bg-sky-600 data-[focus]:text-white flex items-center px-4"
                            >
                              {portfolio.name}
                            </ComboboxOption>
                          ))}
                        </ComboboxOptions>
                      </Combobox>
                      <span className="text-red-600">
                        {
                          idxFeedForm.formState.errors.portfolioFolderId
                            ?.message
                        }
                      </span>
                    </div>
                  </div>
                )}

                {/* Render in case Search Bar */}
                {idxFeedForm.watch("type") === IdxFeedTypeEnum.SEARCH_BAR && (
                  <div className="flex flex-col gap-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-sm">
                          Content*
                        </label>
                        <select
                          className={`h-10 px-4 rounded border border-slate-300`}
                          {...idxFeedForm.register("searchBarContent")}
                        >
                          <option value={"SEARCH_BAR_ONLY"}>
                            Search bar only
                          </option>
                          <option value={"LIST_IN_GRID"}>
                            Search bar with List (Grid Default)
                          </option>
                          <option value={"LIST_IN_MAP"}>
                            Search bar with List (Map Default)
                          </option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-sm">
                          Properties Per Page
                        </label>
                        <select
                          className="h-10 px-4 rounded border border-slate-300"
                          {...idxFeedForm.register("propertiesPerPage", {
                            valueAsNumber: true,
                          })}
                        >
                          <option value={3}>3</option>
                          <option value={6}>6</option>
                          <option value={9}>9</option>
                          <option value={12}>12</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Render alert message if does not have needed information */}
              {(idxFeedForm.watch("type") === IdxFeedTypeEnum.SINGLE_PROPERTY &&
                !idxFeedForm.getValues().propertyId) ||
              (idxFeedForm.watch("type") === "PORTFOLIO" &&
                !idxFeedForm.getValues().portfolioFolderId) ||
              idxFeedForm.formState.isDirty ? (
                <div className="flex items-center py-2 rounded border border-amber-600 bg-amber-50 text-amber-600 px-4">
                  <FiAlertTriangle className="mr-2 shrink-0" />
                  <span>Save your changes to see the updated preview.</span>
                </div>
              ) : null}

              {/* Preview Section */}
              <h2 className="text-lg font-bold">Render Preview</h2>
              <span className="text-slate-600">
                Drag the bottom right corner to resize the preview screen. The
                iframe content is fully responsive and will automatically adjust
                to the available space on your site.
              </span>

              <div className="flex rounded resize overflow-auto h-[600px] outline w-full max-w-screen-xl outline-slate-300">
                <iframe
                  src={iframeSource}
                  allowFullScreen
                  height="100%"
                  width="100%"
                ></iframe>
              </div>

              {/* Code Section */}
              <h2 className="text-lg font-bold">Code</h2>
              <span className="text-slate-600">
                Copy and paste this piece of code on your website.{" "}
                <b>Do not edit this code or it may not work properly.</b>
              </span>
              <div className="bg-slate-800 text-slate-50 rounded p-4 flex w-full">
                <pre lang="HTML">
                  <code className="text-wrap break-all">{iframeCode}</code>
                </pre>
                <div className="flex flex-col items-center relative group ml-4">
                  <button
                    className="flex items-center justify-center h-8 w-8 bg-white bg-opacity-20 hover:bg-sky-600 hover:bg-opacity-100 rounded"
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(iframeCode);
                      toast.success("Code copied!");
                    }}
                  >
                    <FiCopy />
                  </button>
                  <span className="invisible group-hover:visible w-max absolute0 bg-slate-800 rounded h-6 text-sm px-2 flex items-center z-10">
                    Copy Code
                  </span>
                </div>
              </div>

              {/* IDX Analytics Section (Hidden for MVP) */}
              {/* <div className=">
            <h2 className="text-lg font-bold">IDX Analytics</h2>
            <h3 className="text-base font-semibold">
              Property Clicks Analytics
            </h3>
            <div className="flex flex-row justify-start gap-16">
              <div>
                {idxActivityData?.length === 0 ? (
                  <p>No data for the selected range.</p>
                ) : (
                  <IdxActivityLineChart
                    chartData={chartData}
                    dateRange={dateRange}
                    onChartClick={handleChartClick}
                  />
                )}
                <div className= flex flex-row gap-3 items-center">
                  <DateRangePicker
                    dateFormat="MM/dd/yyyy"
                    onChange={handleDateRangeChange}
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                  />
                  <Button
                    onClick={(e) => {
                      e.preventDefault(); // prevent form submission
                      handleResetDateRange();
                    }}
                  >
                    Reset
                  </Button>
                </div>
              </div>
              <div>
                {!tableData && idxActivityData?.length !== 0 && (
                  <p>Click on a point in the chart to view more data!</p>
                )}
                {tableData && (
                  <>
                    <p>Viewing: {targetDate}</p>
                    <table className="text-left">
                      <thead>
                        <tr key="header" className="text-base text-sky-600">
                          <th className="pr-10 py-1">Property</th>
                          <th className="pr-14 py-1">Address</th>
                          <th className="pr-6 py-1">Clicks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData?.propertyClicks?.map((item) => (
                          <tr
                            key={item.propertyId}
                            className="align-text-top border-solid border-b-2 border-collapse"
                          >
                            <td className="pr-10 py-1">{item.name}</td>
                            <td className="pr-14 py-1">{item.address}</td>
                            <td className="pr-6 py-1 text-center">
                              {item.clickCount}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div> */}

              {/* Instructions Section */}
              <h2 className="text-lg font-bold">Need Help?</h2>
              <p className="text-slate-500">
                Need Help?{" "}
                <a
                  href="https://help.dashtodash.com/article/137-getting-started-with-dash-exchange"
                  target="_blank"
                >
                  Read our guides
                </a>{" "}
                to implementing DASH Exchange on major hosting platforms, or{" "}
                <a href="mailto:support@dashtodash.com">contact support</a> for
                assistance.
              </p>
            </>
          )}
        </form>
      </>
    </ModalProvider>
  );
}
